// Feature Flag Service
// Centralized feature visibility control for Broker portal
// Managed exclusively by Lender Admin

// Feature flag storage key
const FEATURE_FLAGS_KEY = 'uff_broker_feature_flags';

// Default feature flags - all features disabled by default for staged rollout
const DEFAULT_FEATURE_FLAGS = {
  // Navigation Features
  scenarioPricer: true,
  compensation: false,
  mismoImportAdvisor: false,
  optimalBluePricing: false,

  // AI Features
  loanAIchatEnabled: false,
  loanAIchatQuickActionsEnabled: false,
  loanAIchatScenarioEnabled: false,
  loanAIchatFeedbackEnabled: false,
  
  // Action Buttons
  pullCreditButton: false,
  orderAppraisalButton: false,
  openTitleButton: false,
  orderVOAButton: false,
  orderVOIEButton: false,
};

// Feature metadata for display in Lender Admin
export const FEATURE_METADATA = {
  // Navigation Features
  scenarioPricer: {
    name: 'Quick Pricer',
    description: 'Loan scenario pricing calculator',
    category: 'Navigation',
    icon: 'ti-calculator'
  },
  compensation: {
    name: 'Compensation',
    description: 'View and manage compensation settings',
    category: 'Navigation',
    icon: 'ti-currency-dollar'
  },
  mismoImportAdvisor: {
    name: 'MISMO Import Advisor',
    description: 'Analyze MISMO 3.4 uploads and recommend product/rate before loan creation',
    category: 'Navigation',
    icon: 'ti-file-analytics'
  },
  optimalBluePricing: {
    name: 'Optimal Blue Pricing',
    description: 'Route loan pricing through Vesta Optimal Blue quote-scenarios instead of Ratesboard',
    category: 'Navigation',
    icon: 'ti-chart-arrows'
  },

  // AI Features
  loanAIchatEnabled: {
    name: 'Loan AI Chat',
    description: 'AI mortgage assistant chat on loan detail page',
    category: 'AI Features',
    icon: 'ti-robot'
  },
  loanAIchatQuickActionsEnabled: {
    name: 'Loan AI Quick Actions',
    description: 'Preset one-click prompts in AI chat',
    category: 'AI Features',
    icon: 'ti-bolt'
  },
  loanAIchatScenarioEnabled: {
    name: 'Loan AI Scenario Modeling',
    description: 'Deterministic scenario projections for AI chat',
    category: 'AI Features',
    icon: 'ti-chart-line'
  },
  loanAIchatFeedbackEnabled: {
    name: 'Loan AI Feedback',
    description: 'Thumbs up/down quality feedback controls',
    category: 'AI Features',
    icon: 'ti-message-circle'
  },

  // Action Buttons
  pullCreditButton: {
    name: 'Pull Credit',
    description: 'Order credit reports',
    category: 'Actions',
    icon: 'ti-credit-card'
  },
  orderAppraisalButton: {
    name: 'Order Appraisal',
    description: 'Order property appraisals',
    category: 'Actions',
    icon: 'ti-home-dollar'
  },
  openTitleButton: {
    name: 'Open Title',
    description: 'Open title orders',
    category: 'Actions',
    icon: 'ti-file-certificate'
  },
  orderVOAButton: {
    name: 'Order VOA',
    description: 'Order verification of assets',
    category: 'Actions',
    icon: 'ti-wallet'
  },
  orderVOIEButton: {
    name: 'Order VOIE',
    description: 'Order verification of income/employment',
    category: 'Actions',
    icon: 'ti-briefcase'
  },
};

// Get all feature flags from localStorage
export const getFeatureFlags = () => {
  try {
    const stored = localStorage.getItem(FEATURE_FLAGS_KEY);
    if (stored) {
      return { ...DEFAULT_FEATURE_FLAGS, ...JSON.parse(stored) };
    }
  } catch (error) {
    console.error('Error loading feature flags:', error);
  }
  return DEFAULT_FEATURE_FLAGS;
};

// Save feature flags to localStorage
export const saveFeatureFlags = (flags) => {
  try {
    localStorage.setItem(FEATURE_FLAGS_KEY, JSON.stringify(flags));
    return true;
  } catch (error) {
    console.error('Error saving feature flags:', error);
    return false;
  }
};

// Check if a specific feature is enabled
export const isFeatureEnabled = (featureKey) => {
  const flags = getFeatureFlags();
  return flags[featureKey] === true;
};

// Toggle a specific feature
export const toggleFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = !flags[featureKey];
  saveFeatureFlags(flags);
  return flags[featureKey];
};

// Enable a specific feature
export const enableFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = true;
  saveFeatureFlags(flags);
};

// Disable a specific feature
export const disableFeature = (featureKey) => {
  const flags = getFeatureFlags();
  flags[featureKey] = false;
  saveFeatureFlags(flags);
};

// Reset all flags to default
export const resetFeatureFlags = () => {
  saveFeatureFlags(DEFAULT_FEATURE_FLAGS);
  return DEFAULT_FEATURE_FLAGS;
};

// Get features grouped by category
export const getFeaturesByCategory = () => {
  const flags = getFeatureFlags();
  const grouped = {};
  
  Object.keys(FEATURE_METADATA).forEach(key => {
    const metadata = FEATURE_METADATA[key];
    const category = metadata.category;
    
    if (!grouped[category]) {
      grouped[category] = [];
    }
    
    grouped[category].push({
      key,
      ...metadata,
      enabled: flags[key]
    });
  });
  
  return grouped;
};

// Export default for easy import
export default {
  getFeatureFlags,
  saveFeatureFlags,
  isFeatureEnabled,
  toggleFeature,
  enableFeature,
  disableFeature,
  resetFeatureFlags,
  getFeaturesByCategory,
  FEATURE_METADATA
};
